import { useLocationPathname, useLocationSearch } from '@rocketlane/rocket-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dotPropImmutable from 'dot-prop-immutable';
import { keyBy } from 'lodash';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCollectionQuery, useMutationFn } from '@rocketlane/state-db';
import { AccountAPI } from 'api/account';
import { CompanyAPI } from 'api/company';
import { DocumentsAPI } from 'api/document/document-core';
import { DocumentSourceType } from 'api/document/types/document';
import { FieldSectionSubType, FieldSectionType } from 'api/field-sections/types';
import { FieldsAPI } from 'api/fields';
import { FieldSectionSubTypeForApi, FieldsObjectTypeForApi } from 'api/fields/fields';
import { HomeAPI } from 'api/home';
import { UserType } from 'api/models';
import { PhaseAPI } from 'api/phase';
import { ProjectAPI } from 'api/project';
import { ObjectType } from 'api/pushes';
import { SpaceAPI } from 'api/space';
import { SprintPlanningAPI } from 'api/sprint-planning';
import { UserAPI } from 'api/user';
import { fetchAllCurrentPhases, fetchPhasesForProject } from 'collections/phase';
import { refreshProjectPlanLanes } from 'collections/plan-board';
import { fetchProjectDetail, fetchProjectsLight, fetchProjectsMeta, fetchArchivedProjects, fetchDeletedProjects } from 'collections/project';
import { addTaskTemplatesToProject, createTaskRecord, duplicateTask, fetchTask, fetchTaskFieldValues, fetchTasksAndMilestones, taskCollection, updateSubTaskOrderRecord } from 'collections/tasks';
import { toastService } from 'components/molecules/toast';
import { FieldDataType } from 'components/organisms/module-settings/fields/types';
import { GENERAL_API_STALE } from 'config/query-config';
import { success } from 'data/current-user-profile';
import { TemplatesAPI } from 'data/templates/api';
import { ProjectTemplatesAPI } from 'data/templates/projects/api';
import { useBudgetFeature } from 'hooks/budgets/use-budget-feature';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useFilterPointsFieldsFromTaskFields } from 'hooks/use-filter-point-fields-from-task-fields';
import { useIsCurrentUserPartner } from 'hooks/use-partner';
import { useHasAccessToSkillMatrix } from 'hooks/use-skill-matrix-permission';
import { invalidateSwrUser } from 'hooks/use-swr-user-hooks';
import { useTranslation } from 'hooks/use-translation';
import { useUser, useUserSafe } from 'hooks/use-user';
import { added } from 'stores/companies';
import { FieldsStore, putAll } from 'stores/fields';
import { SETTINGS_SECTION_FIELDS_QUERY_KEY } from 'utils/project-fields/use-settings-section-fields';
import { migrations } from 'workers/migrations';
import { ACCOUNT_META_DATA_QUERY_KEY, useAccountConfigQuery, useProjectsFromInitData } from './account';
import { getProjectDependencyMapKey, getProjectUsersLiteKey } from './common';
import { ALL_PROJECT_CURRENT_PHASES_QUERY_KEY, ALL_PROJECTS_QUERY_KEY, ALL_PROJECTS_RM_QUERY_KEY, ARCHIVED_PROJECTS_QUERY_KEY, FINANCIAL_FIELDS_QUERY_KEY, fiveMinutes, HOME_QUERY_KEY, oneDay, oneHour, PROJECT_STATUS_FIELD_QUERY_KEY, PROJECTS_META_QUERY_KEY, tenMinutes, SKILL_FIELDS_FOR_READ_ACCESS_QUERY_KEY, DELETED_PROJECTS_QUERY_KEY, CSAT_APPROVAL_COUNT, ALL_EPICS_QUERY_KEY, ALL_APPROVALS_HOME_QUERY_KEY, TIME_ENTRY_FIELDS_QUERY_KEY, TEAM_MEMBER_FIELDS_QUERY_KEY, PROJECT_FIELDS_QUERY_KEY, COMPANY_FIELDS_QUERY_KEY, INTERVALS_FIELDS_QUERY_KEY, KEY_EVENTS_FIELDS_QUERY_KEY, fieldsQueryKeys, EPIC_FIELDS_QUERY_KEY, PROJECT_PHASE_FIELDS_QUERY_KEY, INVOICE_FIELDS_QUERY_KEY, USERS_FIELDS_QUERY_KEY, CUSTOMER_TEAM_MEMBER_FIELDS_QUERY_KEY, EXPENSE_FIELDS_QUERY_KEY, USERS_SKILLS_FIELDS_QUERY_KEY, ALLOCATION_FIELDS_QUERY_KEY, ALL_FIELDS_QUERY_KEY } from './constants';
import { COMPANY_FIELD_SECTIONS_QUERY_KEY, PROJECT_FIELD_SECTIONS_QUERY_KEY_NEW as PROJECT_FIELD_SECTIONS_QUERY_KEY, SPRINT_FIELD_SECTIONS_QUERY_KEY, SKILL_FIELD_SECTIONS_QUERY_KEY, TASK_FIELD_SECTIONS_QUERY_KEY, TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY, CUSTOMER_TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY, INVOICE_FIELD_SECTIONS_QUERY_KEY, BUDGET_FINANCIAL_FIELD_SECTIONS_QUERY_KEY, EPIC_FIELD_SECTIONS_QUERY_KEY, ALLOCATION_FIELD_SECTIONS_QUERY_KEY, TIME_ENTRY_FIELD_SECTIONS_QUERY_KEY } from './field-sections';
import { useAllFieldsBaseQuery, useAllFieldsQueryV2, useTaskFieldsBaseQuery } from './fields';
import { ALL_MEMBERS_QUERY_KEY, ALL_USERS_QUERY_KEY, MEMBERS_V2_MODIFIED_QUERY_KEY, MEMBERS_V2_QUERY_KEY } from './members';
import { useHideNonStrategicPartner } from './partner';
import { PROJECT_TEMPLATE_OPTIONS_QUERY_KEY } from './templates/project-templates';
export const TEMPLATES_QUERY_KEY = 'templates';
export const getTemplatesQueryKey = ()=>[
        TEMPLATES_QUERY_KEY
    ];
export const getStatusUpdateTemplatesQueryKey = ()=>[
        'templates',
        'status-update'
    ];
export const getTemplatesDetailsQueryKey = (templateId)=>{
    return [
        ...getTemplatesQueryKey(),
        templateId
    ];
};
export const getTemplatesPublishQueryKey = (templateId)=>{
    return [
        ...getTemplatesDetailsQueryKey(templateId),
        'publish'
    ];
};
export const getTaskTemplatesQueryKey = ()=>[
        'templates',
        'task'
    ];
export const useTemplatesQuery = (queryConfig = {})=>useQuery(getTemplatesQueryKey(), ()=>TemplatesAPI.getAllTemplates(), {
        staleTime: oneDay,
        ...queryConfig
    });
export const useUserCreatedTaskTemplatesQuery = ()=>{
    const isCurrentUserPartner = useIsCurrentUserPartner();
    return useQuery(getTaskTemplatesQueryKey(), ()=>TemplatesAPI.getUserCreatedTaskTemplates(), {
        enabled: !isCurrentUserPartner
    });
};
export const useDeleteTemplateMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation((id)=>{
        return TemplatesAPI.deleteById(id);
    }, {
        onSuccess: async ()=>{
            await queryClient.invalidateQueries(getTemplatesQueryKey());
            await queryClient.invalidateQueries(PROJECT_TEMPLATE_OPTIONS_QUERY_KEY);
        }
    });
};
export const useDuplicateTemplateMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation((id)=>{
        return TemplatesAPI.cloneById(id);
    }, {
        onSuccess: async ()=>{
            await queryClient.invalidateQueries(getTemplatesQueryKey());
        }
    });
};
export const getProjectMembersQueryKey = ([, projectId])=>[
        'squad',
        projectId
    ];
export const useProjectMembersQuery = (projectId, options = {})=>useQuery(getProjectMembersQueryKey(projectId), ()=>ProjectAPI.getMembers(projectId[1]), {
        staleTime: oneDay,
        ...options
    });
export const getProjectSpacesQueryKey = ([, projectId])=>[
        'spaces',
        projectId
    ];
export const useProjectSpacesQuery = (projectId, options = {})=>{
    return useQuery({
        staleTime: 0,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        ...options,
        queryKey: getProjectSpacesQueryKey(projectId),
        queryFn: ()=>SpaceAPI.getSpaces(projectId[1])
    });
};
export const SpaceDetailQueryKey = (spaceId)=>[
        'space',
        spaceId
    ];
export const useSpaceDetailQuery = (spaceId, { queryKey, queryFn, ...rest } = {})=>useQuery(SpaceDetailQueryKey(spaceId), ()=>SpaceAPI.getSpaceDetail(spaceId), {
        staleTime: oneHour,
        ...rest
    });
export const useProjectsQuery = (options = {})=>{
    const { isDependentEntitiesLoading } = useHideNonStrategicPartner();
    // Wave 1 (additive mirror): merge-push light list into projectCollection.
    return useQuery(ALL_PROJECTS_QUERY_KEY, ()=>fetchProjectsLight(), {
        enabled: !isDependentEntitiesLoading,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        ...options
    });
};
export const useProjectsQueryLookup = ()=>{
    const { data: projects } = useProjectsQuery();
    const lookup = useMemo(()=>keyBy(projects !== null && projects !== void 0 ? projects : [], 'projectId'), [
        projects
    ]);
    return lookup;
};
export const useProjectsForRmQuery = (options = {})=>{
    const { isDependentEntitiesLoading } = useHideNonStrategicPartner();
    return useQuery(ALL_PROJECTS_RM_QUERY_KEY, ()=>ProjectAPI.getProjectsLightForRM(), {
        enabled: !isDependentEntitiesLoading,
        staleTime: oneHour,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        ...options
    });
};
export const ALL_PROJECT_STATUS_MAPPING_QUERY_KEY = [
    'all-project-status-mapping'
];
// central place for the query key
export const PROJECT_STATUS_MAPPING_QUERY_KEY = (projectIds)=>[
        ALL_PROJECT_STATUS_MAPPING_QUERY_KEY,
        projectIds !== null && projectIds !== void 0 ? projectIds : ''
    ];
export const useProjectStatusMappingQuery = (projectIds)=>{
    return useQuery(PROJECT_STATUS_MAPPING_QUERY_KEY(projectIds), ()=>ProjectAPI.getProjectStatusMapping(projectIds !== null && projectIds !== void 0 ? projectIds : ''), {
        staleTime: GENERAL_API_STALE
    });
};
export const ALL_PROJECT_FINANCIAL_CONTRACT_MAPPING_QUERY_KEY = [
    'all-project-project-financial-mapping'
];
export const useProjectFinancailContractMappingQuery = ()=>{
    const isMultiLineBudgetsEnabled = useBudgetFeature();
    return useQuery(ALL_PROJECT_FINANCIAL_CONTRACT_MAPPING_QUERY_KEY, ()=>ProjectAPI.getProjectFinancialContractMapping(), {
        enabled: !isMultiLineBudgetsEnabled
    });
};
export const PROJECT_PARTNER_MAPPING_QUERY_KEY = (projectIds)=>{
    const projectIdsString = projectIds.join(',');
    return [
        'project-partner-mapping',
        projectIdsString
    ];
};
export const useProjectPartnersMappingQuery = (projectIds)=>{
    return useQuery(PROJECT_PARTNER_MAPPING_QUERY_KEY(projectIds), ()=>ProjectAPI.getProjectPartnerMapping(projectIds), {
        enabled: projectIds.length > 0
    });
};
export const PROJECT_PHASES_QUERY_KEY = (projectId)=>[
        `${projectId}_phases`
    ];
export const useProjectsPhasesQuery = (projectId, enabled)=>{
    return useQuery(PROJECT_PHASES_QUERY_KEY(projectId), // Mirrors into `phaseCollection` (state-db-migration-phase.md Wave 1) —
    // same raw return shape, nothing reads from the collection here yet.
    ()=>fetchPhasesForProject(projectId), {
        enabled: projectId != null && enabled
    });
};
export const PROJECT_TASKS_AND_MILESTONES_QUERY_KEY = (projectId)=>[
        'project-tasks-milestones',
        projectId
    ];
export const useProjectTasksAndMilestonesQuery = (projectId, enabled)=>{
    // Collection-backed: fetchTasksAndMilestones pushes tasks into taskCollection;
    // select derives this project's tasks reactively.
    const result = useCollectionQuery({
        collection: taskCollection,
        queryKey: `tasks:and-milestones:${projectId}`,
        queryFn: ()=>fetchTasksAndMilestones(projectId),
        select: (c)=>c.getAll().filter((t)=>{
                var _t_project;
                return ((_t_project = t.project) === null || _t_project === void 0 ? void 0 : _t_project.projectId) === projectId;
            }),
        enabled: projectId != null && enabled
    });
    return result;
};
export const useGetAllProjectCurrentPhases = (options = {})=>{
    return useQuery(ALL_PROJECT_CURRENT_PHASES_QUERY_KEY, // Mirrors into `phaseCollection` (state-db-migration-phase.md Wave 1) —
    // same raw return shape, nothing reads from the collection here yet.
    ()=>fetchAllCurrentPhases(), {
        staleTime: oneHour,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        ...options
    });
};
export const useProjectsMetaQuery = (options = {})=>{
    return useQuery(PROJECTS_META_QUERY_KEY, ()=>fetchProjectsMeta(), {
        staleTime: tenMinutes,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        ...options
    });
};
export const useCreateProjectToTemplateMutation = (projectId, options = {})=>{
    return useMutation(async (payload)=>{
        const template = await ProjectAPI.createProjectToTemplate(projectId, payload);
        return migrations.addEntityOrderToProjectTemplatePhases(template);
    }, {
        ...options
    });
};
export const useArchivedProjectsQuery = (options = {})=>{
    return useQuery(ARCHIVED_PROJECTS_QUERY_KEY, ()=>fetchArchivedProjects(), {
        staleTime: tenMinutes,
        refetchOnWindowFocus: false,
        ...options
    });
};
export const useDeletedProjectsQuery = (options = {})=>{
    return useQuery(DELETED_PROJECTS_QUERY_KEY, ()=>fetchDeletedProjects(), {
        staleTime: tenMinutes,
        refetchOnWindowFocus: false,
        ...options
    });
};
export const useCombinedProjectsData = ()=>{
    const { data: projects } = useProjectsFromInitData();
    const { data: archivedProjects } = useArchivedProjectsQuery();
    const { data: deletedProjects } = useDeletedProjectsQuery();
    const combinedProjectsData = useMemo(()=>{
        var _ref;
        const projectsArray = (_ref = projects === null || projects === void 0 ? void 0 : projects.projects) !== null && _ref !== void 0 ? _ref : [];
        const archivedProjectsArray = archivedProjects || [];
        const deletedProjectsArray = deletedProjects || [];
        return {
            projects: [
                ...projectsArray,
                ...archivedProjectsArray,
                ...deletedProjectsArray
            ],
            byId: {
                ...projects === null || projects === void 0 ? void 0 : projects.byId,
                ...keyBy(archivedProjectsArray, 'projectId'),
                ...keyBy(deletedProjectsArray, 'projectId')
            }
        };
    }, [
        deletedProjects,
        archivedProjects,
        projects
    ]);
    return combinedProjectsData;
};
export const useCompanyTasksQuery = ()=>{
    return useQuery([
        'company-tasks'
    ], ()=>HomeAPI.getMyCompanyTasks(), {
        staleTime: tenMinutes
    });
};
export const useCsatAndApprovalCount = (companyIds)=>{
    return useQuery(CSAT_APPROVAL_COUNT, ()=>HomeAPI.getCsatAndApprovalsCount(companyIds), {
        enabled: Boolean(companyIds === null || companyIds === void 0 ? void 0 : companyIds.length)
    });
};
export const useAllApprovalsQuery = ()=>{
    return useQuery(ALL_APPROVALS_HOME_QUERY_KEY, ()=>HomeAPI.getAllApprovals(), {
        staleTime: tenMinutes
    });
};
export const useHomeQuery = (options = {})=>{
    return useQuery(HOME_QUERY_KEY, ()=>HomeAPI.getHomeScreenResponse(), {
        staleTime: tenMinutes,
        retry: 2,
        refetchOnMount: 'always',
        ...options
    });
};
export const useTaskFieldsQuery = (queryOptions = {})=>{
    return useTaskFieldsBaseQuery({
        staleTime: oneDay,
        ...queryOptions
    });
};
export const useAllFieldsQuery = (queryOptions = {})=>{
    const dispatch = useDispatch();
    const { filterPointsFieldsFromTaskFields } = useFilterPointsFieldsFromTaskFields();
    const fieldsStore = useSelector(({ fields })=>fields);
    return useAllFieldsBaseQuery({
        select: (res)=>filterPointsFieldsFromTaskFields(res),
        ...queryOptions,
        onSuccess: (data)=>{
            if (FieldsStore.loaded(fieldsStore)) {
                return;
            }
            dispatch(putAll(data));
        }
    });
};
export const getCustomFieldUniqueValuesQueryKey = (ids)=>{
    return [
        'custom-field-unique-values',
        ids
    ];
};
export const useCustomTaskFieldValuesForBulkEditor = (ids, shouldFetchCustomFields, options)=>{
    return useQuery(getCustomFieldUniqueValuesQueryKey(ids), ()=>fetchTaskFieldValues(ids), {
        enabled: shouldFetchCustomFields && ids.length > 0,
        refetchOnMount: true,
        ...options
    });
};
export const getFieldUniqueValuesQueryKey = (ids)=>{
    return [
        'field-unique-values',
        ids
    ];
};
export const useProjectFieldValuesForBulkEditor = (ids, options)=>{
    return useQuery(getFieldUniqueValuesQueryKey(ids), ()=>ProjectAPI.getProjectFieldValues(ids), {
        enabled: ids.length > 0,
        refetchOnMount: true,
        ...options
    });
};
export const useProjectFieldsQueryV2 = ({ queryKey, queryFn, ...queryOptions } = {})=>{
    return useQuery({
        ...queryOptions,
        queryKey: PROJECT_FIELDS_QUERY_KEY,
        queryFn: ()=>FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Projects)
    });
};
export const useKeyEventsFieldsQueryV2 = ()=>{
    return useQuery({
        queryKey: KEY_EVENTS_FIELDS_QUERY_KEY,
        queryFn: ()=>FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.KeyEvents, undefined, true)
    });
};
export const useIntervalsFieldsQueryV2 = ({ enabled = true })=>{
    return useQuery({
        queryKey: INTERVALS_FIELDS_QUERY_KEY,
        queryFn: ()=>FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Intervals, undefined, true),
        enabled
    });
};
// export const useProjectFieldsQuery = () => {
//   const [isInvoiceFeatureEnabled] = useAccountFeature(AccountFeature.Invoices);
//   return useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType, objectSubType }) =>
//           objectType === FieldSectionType.Project &&
//           (isInvoiceFeatureEnabled ||
//             objectSubType !== FieldSectionSubType.InvoiceProject),
//       );
//     },
//   });
// };
export const useProjectFieldsQuery = ()=>{
    const [isInvoiceFeatureEnabled] = useAccountFeature(AccountFeature.Invoices);
    return useQuery({
        queryKey: PROJECT_FIELDS_QUERY_KEY,
        queryFn: async ()=>{
            return FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Projects);
        },
        staleTime: oneDay,
        select: (res)=>{
            return ((res === null || res === void 0 ? void 0 : res.fields) || []).filter(({ objectSubType })=>isInvoiceFeatureEnabled || objectSubType !== FieldSectionSubType.InvoiceProject);
        }
    });
};
export const useProjectStatusFieldQueryV2 = ()=>{
    const { data: config } = useAccountConfigQuery();
    return useMemo(()=>{
        var _config_fields;
        return config === null || config === void 0 ? void 0 : (_config_fields = config.fields) === null || _config_fields === void 0 ? void 0 : _config_fields.find((f)=>f.fieldName === 'Status' && f.objectType === FieldSectionType.Project);
    }, [
        config
    ]);
};
// export const useReportFormulaFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType, fieldType }) =>
//           objectType.toString() === 'REPORTS' && fieldType === 'FORMULA',
//       );
//     },
//   });
export const useReportFormulaFieldsQuery = ()=>useAllFieldsBaseQuery({
        staleTime: oneDay,
        select: (res)=>{
            return res.filter(({ objectType, fieldType })=>objectType.toString() === 'REPORTS' && fieldType === 'FORMULA');
        }
    });
export const useProjectStatusFieldQuery = ()=>{
    const { data: projectFields, isLoading } = useProjectFieldsQuery();
    return useQuery(PROJECT_STATUS_FIELD_QUERY_KEY, ()=>{
        if (!projectFields) {
            return undefined;
        }
        return projectFields.find((field)=>field.fieldName === 'Status');
    }, {
        enabled: !isLoading && !!projectFields
    });
};
// export const useEnabledProjectFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType, isEnabled: isFieldEnabled }) =>
//           objectType === FieldSectionType.Project && isFieldEnabled,
//       );
//     },
//   });
export const useEnabledProjectFieldsQuery = ()=>{
    var _ref;
    const { data } = useProjectFieldsQueryV2();
    return ((_ref = data === null || data === void 0 ? void 0 : data.fields) !== null && _ref !== void 0 ? _ref : []).filter(({ isEnabled })=>isEnabled);
};
// export const useTasksFieldsQuery = () => {
//   const { filterPointsFieldsFromTaskFields } =
//     useFilterPointsFieldsFromTaskFields();
//   return useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return filterPointsFieldsFromTaskFields(
//         res.filter(({ objectType }) => objectType === FieldSectionType.Task),
//       ) as Field[];
//     },
//   });
// };
// export const useEnabledTaskFieldsQuery = () => {
//   const { filterPointsFieldsFromTaskFields } =
//     useFilterPointsFieldsFromTaskFields();
//   return useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return filterPointsFieldsFromTaskFields(
//         res.filter(
//           ({ objectType, isEnabled: isFieldEnabled }) =>
//             objectType === FieldSectionType.Task && isFieldEnabled,
//         ),
//       ) as Field[];
//     },
//   });
// };
export const useEnabledTaskFieldsQuery = ()=>{
    const { filterPointsFieldsFromTaskFields } = useFilterPointsFieldsFromTaskFields();
    const { data } = useTaskFieldsBaseQuery();
    // Memoized because the result is a NEW array on every call. Unmemoized, every
    // consumer got a fresh reference each render, invalidating any `useMemo` /
    // `useEffect` / memoized child that takes it as a dependency. In the task
    // pane that showed up as ~10 whole-subtree re-renders during the drawer's
    // open transition, several landing mid-animation. `data` is reference-stable
    // (React Query structural sharing) and the filter callback is `useCallback`ed,
    // so this is now stable across renders.
    return useMemo(()=>filterPointsFieldsFromTaskFields((data !== null && data !== void 0 ? data : []).filter(({ isEnabled })=>isEnabled)), [
        data,
        filterPointsFieldsFromTaskFields
    ]);
};
export const useTeamMemberFieldsQuery = ()=>useQuery({
        queryKey: TEAM_MEMBER_FIELDS_QUERY_KEY,
        queryFn: async ()=>{
            return FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Users);
        },
        staleTime: oneDay,
        select: (data)=>{
            var _ref;
            return (_ref = data === null || data === void 0 ? void 0 : data.fields) !== null && _ref !== void 0 ? _ref : [];
        }
    });
// export const useInheritableTimeEntryFields = () => {
//   const { filterPointsFieldsFromTaskFields } =
//     useFilterPointsFieldsFromTaskFields();
//   return useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       const filteredFields = res.filter(
//         ({ objectType, isEnabled }) =>
//           [
//             FieldSectionType.Project,
//             FieldSectionType.Task,
//             FieldSectionType.Users,
//             FieldSectionType.Company,
//           ].includes(objectType) && isEnabled,
//       );
//       // Transform projectOwner field's fieldDataType from NUMBER to USER
//       const transformedFields = filteredFields.map((field) => {
//         if (
//           field.fieldName === 'projectOwner' &&
//           field.objectType === FieldSectionType.Project &&
//           field.fieldDataType === 'NUMBER'
//         ) {
//           return {
//             ...field,
//             fieldDataType: 'USER',
//           };
//         }
//         return field;
//       });
//       return filterPointsFieldsFromTaskFields(transformedFields) as Field[];
//     },
//   });
// };
export const useInheritableTimeEntryFieldsV2 = ()=>{
    const { filterPointsFieldsFromTaskFields } = useFilterPointsFieldsFromTaskFields();
    const projectFields = useAllFieldsQueryV2({
        objectType: FieldSectionType.Project
    }, {
        staleTime: oneDay
    });
    const taskFields = useTaskFieldsBaseQuery({
        staleTime: oneDay
    });
    const usersFields = useAllFieldsQueryV2({
        objectType: FieldSectionType.Users
    }, {
        staleTime: oneDay
    });
    const companyFields = useAllFieldsQueryV2({
        objectType: FieldSectionType.Company
    }, {
        staleTime: oneDay
    });
    const queries = [
        projectFields,
        taskFields,
        usersFields,
        companyFields
    ];
    const isLoading = queries.some((query)=>query.isLoading);
    const data = useMemo(()=>{
        if (isLoading) {
            return undefined;
        }
        const enabledFields = queries.flatMap((q)=>{
            var _q_data;
            return (_q_data = q.data) !== null && _q_data !== void 0 ? _q_data : [];
        }).filter((f)=>f.isEnabled);
        // Transform projectOwner field's fieldDataType from NUMBER to USER
        const transformedFields = enabledFields.map((field)=>{
            if (field.fieldName === 'projectOwner' && field.objectType === FieldSectionType.Project && field.fieldDataType === 'NUMBER') {
                return {
                    ...field,
                    fieldDataType: 'USER'
                };
            }
            return field;
        });
        return filterPointsFieldsFromTaskFields(transformedFields);
    }, [
        isLoading,
        projectFields.data,
        taskFields.data,
        usersFields.data,
        companyFields.data
    ]);
    return {
        data,
        isLoading
    };
};
// export const useTimeEntryFieldsQuery = () => {
//   return useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(({ objectType, objectSubType }) => {
//         return (
//           objectType === FieldSectionType.TimeEntry &&
//           objectSubType === FieldSectionSubType.Default
//         );
//       });
//     },
//   });
// };
// export const useTimeEntryFieldsQueryV2 = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(({ objectType, objectSubType }) => {
//         return (
//           objectType === FieldSectionType.TimeEntry &&
//           objectSubType === FieldSectionSubType.Default
//         );
//       });
//     },
//   });
export const useTimeEntryCustomFieldsQueryV2 = ()=>{
    return useQuery({
        queryKey: TIME_ENTRY_FIELDS_QUERY_KEY,
        queryFn: async ()=>{
            return FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.TimeEntry);
        },
        staleTime: oneDay,
        select: (data)=>{
            var _ref;
            return (_ref = data === null || data === void 0 ? void 0 : data.fields) !== null && _ref !== void 0 ? _ref : [];
        }
    });
};
export const useExpenseFieldsQuery = ()=>useQuery({
        queryKey: EXPENSE_FIELDS_QUERY_KEY,
        queryFn: async ()=>{
            const resp = await FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Expense);
            return (resp === null || resp === void 0 ? void 0 : resp.fields) || [];
        },
        staleTime: oneDay
    });
export const useAllocationFieldsQuery = ()=>useQuery({
        queryKey: ALLOCATION_FIELDS_QUERY_KEY,
        queryFn: ()=>FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.ResourceAllocation),
        select: (data)=>{
            var _ref;
            return (_ref = data === null || data === void 0 ? void 0 : data.fields) !== null && _ref !== void 0 ? _ref : [];
        },
        staleTime: oneDay
    });
// export const useExpenseFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(({ objectType, objectSubType }) => {
//         return (
//           objectType === FieldSectionType.Expense &&
//           objectSubType === FieldSectionSubType.Default
//         );
//       });
//     },
//   });
// export const useCustomerTeamMemberFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType, objectSubType }) =>
//           objectType === FieldSectionType.Users &&
//           objectSubType === FieldSectionSubType.ExternalUser,
//       );
//     },
//   });
export const useCustomerTeamMemberFieldsQuery = ()=>useQuery({
        queryKey: CUSTOMER_TEAM_MEMBER_FIELDS_QUERY_KEY,
        queryFn: async ()=>{
            const resp = await FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Users, FieldSectionSubTypeForApi.ExternalUser);
            return (resp === null || resp === void 0 ? void 0 : resp.fields) || [];
        },
        staleTime: oneDay
    });
// export const useTeamMemberUserFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({
//           objectType,
//           objectSubType,
//           fieldDataType,
//           isEnabled: isFieldEnabled,
//         }) =>
//           objectType === FieldSectionType.Users &&
//           objectSubType === FieldSectionSubType.Default &&
//           fieldDataType === FieldDataType.User &&
//           isFieldEnabled,
//       );
//     },
//   });
export const useTeamMemberUserFieldsQuery = ()=>{
    return useQuery({
        queryKey: USERS_FIELDS_QUERY_KEY,
        queryFn: async ()=>{
            const resp = await FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Users);
            return (resp === null || resp === void 0 ? void 0 : resp.fields) || [];
        },
        select: (resp)=>resp.filter(({ fieldDataType, isEnabled })=>fieldDataType === FieldDataType.User && isEnabled),
        staleTime: oneDay
    });
};
const useSkillFieldsQueryForReadAccess = (resource)=>{
    const hasAccessToSkillMatrix = useHasAccessToSkillMatrix();
    return useQuery([
        SKILL_FIELDS_FOR_READ_ACCESS_QUERY_KEY,
        resource
    ], ()=>FieldsAPI.getSkillFieldIdsForReadAccess(resource), {
        enabled: hasAccessToSkillMatrix
    });
};
export const useSkillFieldsQuery = (resource)=>{
    const hasAccessToSkillMatrix = useHasAccessToSkillMatrix();
    const { data: skillFieldIds } = useSkillFieldsQueryForReadAccess(resource);
    return useQuery({
        queryKey: USERS_SKILLS_FIELDS_QUERY_KEY,
        queryFn: async ()=>{
            return FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Users, FieldSectionSubTypeForApi.Skill);
        },
        staleTime: oneDay,
        select: (data)=>{
            return data === null || data === void 0 ? void 0 : data.fields.filter(({ fieldId })=>hasAccessToSkillMatrix && (skillFieldIds === null || skillFieldIds === void 0 ? void 0 : skillFieldIds.includes(fieldId)));
        }
    });
};
// export const useInvoiceFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType }) => objectType === FieldSectionType.Invoice,
//       );
//     },
//   });
export const useInvoiceFieldsQuery = ()=>{
    return useQuery({
        queryKey: INVOICE_FIELDS_QUERY_KEY,
        queryFn: async ()=>{
            const resp = await FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Invoice);
            return (resp === null || resp === void 0 ? void 0 : resp.fields) || [];
        },
        staleTime: oneDay
    });
};
// export const useCreditNoteFieldsQuery = () =>
//   useQuery({
//     queryKey: CREDIT_NOTE_FIELDS_QUERY_KEY,
//     queryFn: async () => {
//       const resp = await FieldsAPI.getFieldsByObjectType(
//         FieldsObjectTypeForApi.CreditNote,
//       );
//       return resp?.fields || [];
//     },
//     staleTime: oneDay,
//   });
// TODO: Remove this once the API is updated
export const useCreditNoteFieldsQuery = ()=>useAllFieldsBaseQuery({
        staleTime: oneDay,
        select: (res)=>{
            return res.filter(({ objectType })=>objectType === FieldSectionType.CreditNote);
        }
    });
// export const useCompanyFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType }) => objectType === FieldSectionType.Company,
//       );
//     },
//   });
export const useCompanyFieldsQuery = ()=>{
    return useQuery({
        queryKey: COMPANY_FIELDS_QUERY_KEY,
        queryFn: async ()=>{
            return FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Company);
        },
        staleTime: oneDay,
        select: (data)=>{
            var _ref;
            return (_ref = data === null || data === void 0 ? void 0 : data.fields) !== null && _ref !== void 0 ? _ref : [];
        }
    });
};
// export const useEnabledCompanyFieldsQuery = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (res) => {
//       return res.filter(
//         ({ objectType, isEnabled: isFieldEnabled }) =>
//           isFieldEnabled,
//       );
//     },
//   });
export const useEnabledCompanyFieldsQuery = ()=>{
    const { data } = useCompanyFieldsQuery();
    return (data !== null && data !== void 0 ? data : []).filter(({ isEnabled })=>isEnabled);
};
// export const useProjectPhaseFields = () =>
//   useAllFieldsBaseQuery({
//     staleTime: oneDay,
//     select: (response) => {
//       return response.filter(
//         ({ objectType }) => objectType === FieldSectionType.ProjectPhase,
//       );
//     },
//   });
export const useProjectPhaseFields = ()=>{
    return useQuery({
        queryKey: PROJECT_PHASE_FIELDS_QUERY_KEY,
        queryFn: async ()=>{
            const projectPhaseFields = await FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.ProjectPhase);
            return (projectPhaseFields === null || projectPhaseFields === void 0 ? void 0 : projectPhaseFields.fields) || [];
        },
        cacheTime: 0
    });
};
export const useFinancialFieldsQuery = (queryOptions = {})=>{
    return useQuery(FINANCIAL_FIELDS_QUERY_KEY, async ()=>{
        const res = await FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Financial);
        const fields = (res === null || res === void 0 ? void 0 : res.fields) || [];
        if (fields.length === 0) {
            return {};
        }
        return keyBy(fields, 'fieldName');
    }, {
        staleTime: oneDay,
        ...queryOptions
    });
};
export const useFinancialFieldsV2 = ()=>{
    const query = useQuery([
        FINANCIAL_FIELDS_QUERY_KEY,
        'paginated'
    ], async ()=>{
        const resp = await FieldsAPI.getPaginatedFields(ObjectType.ProjectFinanicalField, {
            offset: 0,
            limit: 50,
            includedFieldNames: [
                'costRate',
                'projectCustomBillRate',
                'projectRateCardUpdate',
                'projectBudget',
                'projectHours',
                'projectProfitability'
            ]
        });
        return keyBy(resp === null || resp === void 0 ? void 0 : resp.data, 'fieldName');
    });
    return query;
};
export const useBudgetFinancialFieldsQuery = (enabled = true)=>{
    return useAllFieldsBaseQuery({
        staleTime: oneDay,
        enabled,
        select: (res)=>{
            return res.filter(({ objectType })=>objectType === FieldSectionType.Budget);
        }
    });
};
// export const useBudgetFinancialFieldsQuery = () => {
//   return useQuery({
//     queryKey: FINANCIAL_BUDGET_FIELDS_QUERY_KEY,
//     queryFn: async () => {
//       return FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Budget);
//     },
//     staleTime: oneDay,
//     select: (data) => data?.fields ?? [],
//   });
// };
export const useSprintFieldsQuery = (queryOptions = {})=>useAllFieldsBaseQuery({
        cacheTime: 0,
        select: (res)=>{
            return res.filter(({ objectType })=>objectType === FieldSectionType.Sprint);
        },
        ...queryOptions
    });
export const useEpicsQuery = (options = {})=>{
    const { isDependentEntitiesLoading } = useHideNonStrategicPartner();
    return useQuery(ALL_EPICS_QUERY_KEY, ()=>SprintPlanningAPI.getEpicsList(), {
        enabled: !isDependentEntitiesLoading,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        ...options
    });
};
// export const useEpicFieldsQuery = (queryOptions = {}) =>
//   useAllFieldsBaseQuery({
//     cacheTime: 0,
//     select: (res) => {
//       return res.filter(
//         ({ objectType }) => objectType === FieldSectionType.Epic,
//       );
//     },
//     ...queryOptions,
//   });
export const useEpicFieldsQuery = (queryOptions = {})=>{
    return useQuery({
        queryKey: EPIC_FIELDS_QUERY_KEY,
        queryFn: async ()=>{
            const epicFields = await FieldsAPI.getFieldsByObjectType(FieldsObjectTypeForApi.Epic);
            return (epicFields === null || epicFields === void 0 ? void 0 : epicFields.fields) || [];
        },
        cacheTime: 0,
        ...queryOptions
    });
};
export const useFieldMutation = (mutationFn, options = {})=>{
    const queryClient = useQueryClient();
    return useMutation(mutationFn, {
        ...options,
        onSuccess: async (data, variables, context)=>{
            await queryClient.invalidateQueries([
                SETTINGS_SECTION_FIELDS_QUERY_KEY
            ]);
            await queryClient.invalidateQueries(ALL_FIELDS_QUERY_KEY);
            await queryClient.invalidateQueries(fieldsQueryKeys[data.objectType]);
            if (data && data.objectType === FieldSectionType.Task) {
                await queryClient.invalidateQueries(TASK_FIELD_SECTIONS_QUERY_KEY);
            }
            if (data && data.objectType === FieldSectionType.Company) {
                await queryClient.invalidateQueries(COMPANY_FIELD_SECTIONS_QUERY_KEY);
            }
            if ((data === null || data === void 0 ? void 0 : data.objectType) === FieldSectionType.ResourceAllocation) {
                await queryClient.invalidateQueries(ALLOCATION_FIELD_SECTIONS_QUERY_KEY);
            }
            if ((data === null || data === void 0 ? void 0 : data.objectType) === FieldSectionType.Project) {
                await queryClient.invalidateQueries(PROJECT_FIELD_SECTIONS_QUERY_KEY);
            }
            if (data.objectType === FieldSectionType.Sprint) {
                await queryClient.invalidateQueries(SPRINT_FIELD_SECTIONS_QUERY_KEY);
            }
            if (data.objectType === FieldSectionType.Epic) {
                await queryClient.invalidateQueries(EPIC_FIELD_SECTIONS_QUERY_KEY);
            }
            if (data.objectType === FieldSectionType.Invoice) {
                await queryClient.invalidateQueries(INVOICE_FIELD_SECTIONS_QUERY_KEY);
            }
            if (data.objectType === FieldSectionType.Budget) {
                await queryClient.invalidateQueries(BUDGET_FINANCIAL_FIELD_SECTIONS_QUERY_KEY);
            }
            if (data.objectType === FieldSectionType.TimeEntry) {
                await queryClient.invalidateQueries(TIME_ENTRY_FIELD_SECTIONS_QUERY_KEY);
            }
            if (data.objectType === FieldSectionType.Users) {
                if (data.objectSubType === FieldSectionSubType.Skill) {
                    await queryClient.invalidateQueries(SKILL_FIELD_SECTIONS_QUERY_KEY);
                    await queryClient.invalidateQueries([
                        SKILL_FIELDS_FOR_READ_ACCESS_QUERY_KEY
                    ]);
                    await queryClient.refetchQueries([
                        SKILL_FIELDS_FOR_READ_ACCESS_QUERY_KEY
                    ]);
                    await queryClient.refetchQueries([
                        [
                            'skill-fields-for-read'
                        ],
                        null
                    ]);
                    await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
                } else if (data.objectSubType === FieldSectionSubType.ExternalUser) {
                    await queryClient.invalidateQueries(CUSTOMER_TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY);
                } else {
                    await queryClient.invalidateQueries(TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY);
                }
            }
            if (options.onSuccess) {
                await options.onSuccess(data, variables, context);
            }
        }
    });
};
export const GET_FIELD_BY_ID_QUERY_KEY = (fieldId)=>[
        'GET_FIELD_BY_ID_QUERY',
        fieldId
    ];
export const useGetFieldByIdQuery = (fieldId, queryOptions = {})=>useQuery(GET_FIELD_BY_ID_QUERY_KEY(fieldId), ()=>FieldsAPI.getField(fieldId), {
        staleTime: oneDay,
        ...queryOptions
    });
export const useCreateFieldMutation = (options = {})=>{
    return useFieldMutation((payload)=>FieldsAPI.createField(payload), options);
};
export const useUpdateFieldMutation = (fieldId, options = {})=>{
    const queryClient = useQueryClient();
    return useFieldMutation((payload)=>FieldsAPI.updateField(fieldId, payload), {
        ...options,
        onSuccess: (data, variables, context)=>{
            var _options_onSuccess;
            (_options_onSuccess = options.onSuccess) === null || _options_onSuccess === void 0 ? void 0 : _options_onSuccess.call(options, data, variables, context);
            // Todo @Muskaan - Remove below query once pagination goes out
            queryClient.invalidateQueries(ALL_PROJECTS_RM_QUERY_KEY);
            queryClient.invalidateQueries(ALLOCATION_FIELDS_QUERY_KEY);
        }
    });
};
export const useDeleteFieldMutation = (fieldId)=>{
    return useFieldMutation(()=>FieldsAPI.deleteField(fieldId));
};
export const useGetFieldProjectMapping = (fieldId)=>{
    return useMutation(({ value })=>FieldsAPI.getFieldProjectMapping(fieldId, value), {
        onSuccess: async (data)=>{
            return data;
        }
    });
};
const ACCOUNT_META_QUERY_KEY = [
    'account-meta'
];
export const useAccountMetaQuery = (queryOptions = {})=>{
    const pathname = useLocationPathname();
    const search = useLocationSearch();
    const searchParams = new URLSearchParams(search);
    const domain = searchParams.get('domain');
    return useQuery(ACCOUNT_META_QUERY_KEY, ()=>AccountAPI.getMeta(domain, pathname), {
        staleTime: oneDay,
        refetchOnWindowFocus: false,
        ...queryOptions
    });
};
export const useCompanyUpdateMutation = (companyId)=>{
    return useMutation((payload)=>CompanyAPI.updateCompany(companyId, payload));
};
export const PROJECT_TEMPLATE_QUERY_KEY = [
    'project_templates'
];
export const useProjectTemplates = (options = {})=>{
    const isCurrentUserPartner = useIsCurrentUserPartner();
    const queryResult = useQuery(PROJECT_TEMPLATE_QUERY_KEY, ()=>{
        return ProjectTemplatesAPI.getAllTemplates();
    }, {
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: !isCurrentUserPartner,
        ...options
    });
    // Combine both loading states into a single boolean
    const isLoadingState = !isCurrentUserPartner ? queryResult.isLoading : queryResult.isInitialLoading;
    // Return the original query result with the additional isLoadingState
    return {
        ...queryResult,
        isLoading: isLoadingState
    };
};
const GET_PHASES_PROJECT_TEMPLATE_QUERY_KEY = [
    'phases_project_templates'
];
export const useGetPhasesProjectTemplatesQuery = (options = {})=>{
    return useQuery(GET_PHASES_PROJECT_TEMPLATE_QUERY_KEY, ()=>{
        return ProjectTemplatesAPI.getPhases();
    }, {
        staleTime: fiveMinutes,
        refetchOnWindowFocus: false,
        ...options
    });
};
export const useCreateCompanyMutation = (options = {})=>{
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useMutation((payload)=>CompanyAPI.createCompany(payload), {
        onSuccess: async (company)=>{
            dispatch(added(company));
            await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        },
        ...options
    });
};
export const useCreateUserMutation = (options = {})=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>UserAPI.createUser(payload), {
        onSuccess: async ()=>{
            await queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
        },
        ...options
    });
};
export const useAccountUpdateMutation = (options = {})=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>AccountAPI.updateAccount(payload), {
        onSuccess: async ()=>{
            await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        },
        ...options
    });
};
export const useOutOfOfficeUpdateMutation = (options = {})=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>AccountAPI.updateOutOfOffice(payload), {
        onSuccess: async ()=>{
            await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
        },
        ...options
    });
};
export const useBulkUserCreateMutation = (options = {})=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>UserAPI.bulkCreate(payload), {
        onSuccess: async ()=>{
            await queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
        },
        ...options
    });
};
export const useAvatarUploadMutation = (options = {})=>{
    const { user } = useUser();
    return useMutation((payload)=>UserAPI.uploadProfilePic(payload), {
        ...options,
        onSuccess: (...args)=>{
            var _options_onSuccess;
            if (user === null || user === void 0 ? void 0 : user.userId) {
                invalidateSwrUser(user.userId);
            }
            (_options_onSuccess = options.onSuccess) === null || _options_onSuccess === void 0 ? void 0 : _options_onSuccess.call(options, ...args);
        }
    });
};
export const useCompanyLogoUploadMutation = (options = {})=>{
    return useMutation((payload)=>CompanyAPI.uploadPicture(payload), options);
};
export const getCompanyAddressQueryKey = (companyId, source)=>{
    return [
        'company-address',
        companyId,
        source
    ];
};
export const getSpecificCompanyAddressQueryKey = (companyId, companyAddressId, source)=>{
    return [
        'company-address-id',
        companyId,
        companyAddressId,
        source
    ];
};
export const useCompanyAddressQuery = (companyId, source = 'ACCOUNT', options = {})=>{
    return useQuery(getCompanyAddressQueryKey(companyId, source), ()=>CompanyAPI.getCompanyAddresses(companyId, source), {
        staleTime: GENERAL_API_STALE,
        ...options
    });
};
export const useSpecificCompanyAddressQuery = (companyId, companyAddressId, source = 'ACCOUNT', options = {})=>{
    return useQuery(getSpecificCompanyAddressQueryKey(companyId, companyAddressId, source), ()=>CompanyAPI.getSpecificCompanyAddress(companyId, companyAddressId, source), {
        staleTime: GENERAL_API_STALE,
        ...options
    });
};
export const useUpdateCompanyAddressMutation = ()=>{
    return useMutation(({ companyId, addressId, payload })=>CompanyAPI.editAddress(companyId, addressId, payload));
};
export const useCreateCompanyAddressMutation = ()=>{
    return useMutation(({ companyId, payload })=>CompanyAPI.createAddress(companyId, payload));
};
export const useUpdateUserMetaMutation = (options = {})=>{
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    return useMutation((payload)=>UserAPI.updateMeta(payload), {
        onSuccess: async ()=>{
            /**
         * Since context provider is removed as part of perf optimization
         * https://github.com/Rocketlane/rocket-frontend/pull/6299
         * query key is not active, hence doing the following changes,
         * along with query invalidation.
         */ await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
            const accountMeta = queryClient.getQueryData(ACCOUNT_META_DATA_QUERY_KEY);
            if (accountMeta === null || accountMeta === void 0 ? void 0 : accountMeta.userProfile) {
                dispatch(success(accountMeta.userProfile));
            }
        },
        ...options
    });
};
export const useOptimisticUpdateUserMetaMutation = (options = {})=>{
    const dispatch = useDispatch();
    const usersData = useUser();
    return useMutation((payload)=>UserAPI.updateMeta(payload), {
        onMutate: (payload)=>{
            if (usersData === null || usersData === void 0 ? void 0 : usersData.userMeta) {
                const data = dotPropImmutable.set(usersData, 'userMeta.metaData', payload.metaData);
                dispatch(success(data));
            }
            return usersData;
        },
        onError: (error, _, previousData)=>{
            if (previousData) {
                dispatch(success(previousData));
            }
        },
        ...options
    });
};
/**
 * Note: remove this mutation (useUpdateCurrentUserMutation) and do not use it.
 * */ export const useUpdateCurrentUserMutation = (options = {})=>{
    const queryClient = useQueryClient();
    const { user } = useUser();
    return useMutation((payload)=>UserAPI.updateCurrentUser(payload), {
        ...options,
        onSuccess: async (...args)=>{
            var _options_onSuccess;
            await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
            if (user === null || user === void 0 ? void 0 : user.userId) {
                invalidateSwrUser(user.userId);
            }
            await ((_options_onSuccess = options.onSuccess) === null || _options_onSuccess === void 0 ? void 0 : _options_onSuccess.call(options, ...args));
        }
    });
};
export const useCompanyLogoQuery = (params)=>{
    return useQuery([
        'company-domain',
        params
    ], ({ queryKey })=>CompanyAPI.getCompanyLogoUrl(queryKey[1].companyDomain), {
        enabled: params.companyDomain.length > 0,
        staleTime: tenMinutes,
        refetchOnWindowFocus: false
    });
};
export const useCsatQuery = (companyId)=>{
    var _ref;
    var _userData_user;
    const userData = useUserSafe();
    return useQuery([
        'CSAT'
    ], ()=>HomeAPI.getCsat(companyId), {
        enabled: [
            UserType.External,
            UserType.CustomerPortalBot,
            UserType.CustomerPartner
        ].includes((_ref = userData === null || userData === void 0 ? void 0 : (_userData_user = userData.user) === null || _userData_user === void 0 ? void 0 : _userData_user.userType) !== null && _ref !== void 0 ? _ref : UserType.External),
        staleTime: fiveMinutes
    });
};
export const useProjectsCountQuery = (filter, user, enabled)=>{
    return useQuery([
        'projects-count'
    ], async ()=>{
        const { KanbanApi } = await import('api/tasks/kanban');
        return KanbanApi.getProjectsCountByUser({
            filter
        });
    }, {
        onSuccess: async (data)=>{
            const projects = user.projects.reduce((obj, nextItem)=>{
                obj[nextItem.projectId] = {
                    customer: nextItem.customer,
                    projectName: nextItem.projectName
                };
                return obj;
            }, {});
            const result = data.reduce((acc, project)=>{
                var _projects_projectId, _projects_projectId_customer, _projects_projectId1, _projects_projectId_customer1, _projects_projectId2;
                const { projectId, projectName: _projectName, count } = project;
                const projectName = _projectName || ((_projects_projectId = projects[projectId]) === null || _projects_projectId === void 0 ? void 0 : _projects_projectId.projectName);
                const companyName = (_projects_projectId1 = projects[projectId]) === null || _projects_projectId1 === void 0 ? void 0 : (_projects_projectId_customer = _projects_projectId1.customer) === null || _projects_projectId_customer === void 0 ? void 0 : _projects_projectId_customer.companyName;
                const companyLogoUrl = (_projects_projectId2 = projects[projectId]) === null || _projects_projectId2 === void 0 ? void 0 : (_projects_projectId_customer1 = _projects_projectId2.customer) === null || _projects_projectId_customer1 === void 0 ? void 0 : _projects_projectId_customer1.companyLogoUrl;
                acc[projectId] = {
                    projectId,
                    projectName,
                    count,
                    companyName,
                    companyLogoUrl
                };
                return acc;
            }, {});
            return result;
        },
        enabled
    });
};
export const useProjectMergeMutation = (projectId, options = {})=>{
    return useMutation((payload)=>ProjectAPI.merge(projectId[1], payload), {
        onSuccess: async ()=>{
            // Detail is collection-backed — refetch+push.
            await fetchProjectDetail(projectId[1]);
        // Task-list RQ cache is dead (collection-backed) — the previous
        // invalidateQueries(getProjectTasksQueryKey) was a no-op and is removed.
        },
        ...options
    });
};
export const getDocumentsQueryKey = ([, projectId])=>[
        'documents',
        projectId
    ];
export const useDocumentsQuery = (projectId)=>{
    return useQuery(getDocumentsQueryKey(projectId), ()=>DocumentsAPI.getDocumentsList(projectId[1]), {
        staleTime: tenMinutes
    });
};
export const useGetDocumentForSpaceTab = (projectId, spaceId)=>{
    const { data = [] } = useDocumentsQuery([
        'project_id',
        projectId
    ]);
    const doc = useMemo(()=>{
        return data.find((d)=>{
            var _d_source, _d_source1;
            return ((_d_source = d.source) === null || _d_source === void 0 ? void 0 : _d_source.sourceId) === spaceId && (d === null || d === void 0 ? void 0 : (_d_source1 = d.source) === null || _d_source1 === void 0 ? void 0 : _d_source1.sourceType) === DocumentSourceType.SpaceTab;
        });
    }, [
        data,
        spaceId
    ]);
    return doc;
};
export const useAddExistingTasksMutation = (projectId, options = {})=>{
    return useMutation(({ tasks, meta })=>DocumentsAPI.addExistingSectionTask(projectId, tasks, meta), {
        ...options
    });
};
export const useAddMembersToProjectMutation = ()=>{
    const successTitle = useTranslation('invitation.team_member.success.title');
    const successMessage = useTranslation('invitation.team_member.success.content');
    const errorTitle = useTranslation('invitation.team_member.error.title');
    const errorMessage = useTranslation('invitation.team_member.error.content');
    const queryClient = useQueryClient();
    return useMutation(({ projectId, payload })=>ProjectAPI.addMembers(projectId[1], payload), {
        onSuccess: async (_, { projectId, suppressToast })=>{
            await queryClient.invalidateQueries(getProjectMembersQueryKey(projectId));
            await queryClient.invalidateQueries(getProjectUsersLiteKey(projectId[1]));
            // Invalidate budget exclusions queries for the project
            await queryClient.invalidateQueries([
                'project-members-exclusion',
                projectId
            ]);
            await queryClient.invalidateQueries([
                'budget-member-exclusion'
            ]);
            await queryClient.invalidateQueries(MEMBERS_V2_QUERY_KEY);
            await queryClient.invalidateQueries(MEMBERS_V2_MODIFIED_QUERY_KEY);
            if (suppressToast !== true) {
                toastService({
                    title: successTitle,
                    message: successMessage
                }).success();
            }
        },
        onError: (_, { suppressToast })=>{
            if (suppressToast !== true) {
                toastService({
                    title: errorTitle,
                    message: errorMessage
                }).error();
            }
        }
    });
};
export const GET_EMAIL_CONFIG = [
    'getEmailConfig'
];
export const useGetDKIMConfig = ()=>{
    return useQuery(GET_EMAIL_CONFIG, ()=>AccountAPI.getEmailConfig(), {
        retry: 1,
        staleTime: fiveMinutes
    });
};
export const useFTUEMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload)=>AccountAPI.toggleFTUE(payload),
        onSuccess: async ()=>{
            await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
            // Todo @Muskaan - Remove below query once pagination goes out
            await queryClient.invalidateQueries(ALL_PROJECTS_RM_QUERY_KEY);
            await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
        }
    });
};
export const useCreateTaskMutation = ([, projectId])=>{
    return useMutation((payload)=>createTaskRecord(projectId, payload), {
        onSuccess: async (data)=>{
            if (data.projectPhase) {
                var _ref;
                var _data_project;
                const taskProjectId = (_ref = (_data_project = data.project) === null || _data_project === void 0 ? void 0 : _data_project.projectId) !== null && _ref !== void 0 ? _ref : projectId;
                if (data.taskId && taskProjectId) {
                    // POST create may return a slim task (e.g. missing formInstanceId).
                    await fetchTask(taskProjectId, data.taskId);
                }
                // Detail is collection-backed — refetch+push (phase task counts).
                await fetchProjectDetail(projectId);
                // Paginated plan lanes re-read their canonical entity order
                // (collection-backed, batch-coalesced, never rejects).
                refreshProjectPlanLanes(projectId);
            }
        }
    });
};
export const useAddTaskTemplateMutation = ([, projectId])=>{
    // Detail refresh is collection-backed via fetchProjectDetail; task data lands
    // reactively via addTaskTemplatesToProject → taskCollection.push.
    return useMutationFn({
        mutationFn: (payload)=>addTaskTemplatesToProject(projectId, payload),
        onSuccess: async (data)=>{
            var _data_;
            if ((_data_ = data[0]) === null || _data_ === void 0 ? void 0 : _data_.projectPhase) {
                // Detail is collection-backed — refetch+push (phase task counts).
                await fetchProjectDetail(projectId);
            }
            // Paginated plan lanes re-read their canonical entity order
            // (collection-backed, batch-coalesced, never rejects).
            refreshProjectPlanLanes(projectId);
        }
    });
};
export const useDuplicateTaskMutation = ([, projectId])=>{
    const duplicationInProgress = useTranslation('task.duplicate_task.in_progress');
    const notifyDuplication = useTranslation('task.duplicate_task.notify');
    // queryClient kept ONLY for the cross-entity dependency-map cache.
    // duplicateTask returns void; the new tasks are re-fetched and pushed into
    // taskCollection separately (reactive), and project detail is collection-backed.
    const queryClient = useQueryClient();
    return useMutationFn({
        mutationFn: (payload)=>duplicateTask(projectId, payload),
        onSuccess: async ()=>{
            toastService({
                title: duplicationInProgress,
                message: notifyDuplication
            }).success();
            // Detail is collection-backed — refetch+push.
            fetchProjectDetail(projectId);
            await queryClient.invalidateQueries(getProjectDependencyMapKey(projectId));
        }
    });
};
/**
 * Custom domain queries
 */ export const GET_CUSTOM_DOMAIN_QUERY_KEY = [
    'getCustomDomainQuery'
];
export const useCustomDomainConfigQuery = ()=>{
    return useQuery(GET_CUSTOM_DOMAIN_QUERY_KEY, ()=>AccountAPI.getCustomDomain(), {
        retry: 1,
        staleTime: fiveMinutes,
        refetchOnMount: true
    });
};
export const useUpdateEntityOrderOfPhaseMutation = (phaseId)=>{
    return useMutation((payload)=>PhaseAPI.updateEntityOrder(phaseId, payload));
};
export const useUpdateSubTaskOrderMutation = (taskId)=>{
    // updateSubTaskOrderRecord returns void; the affected task is re-fetched and
    // pushed into taskCollection separately (reactive). No cross-entity caches.
    return useMutationFn({
        mutationFn: (payload)=>updateSubTaskOrderRecord(taskId, payload)
    });
};
export const useProjectMembersMetaQuery = ([, projectId], options = {})=>{
    return useQuery([
        'members-meta',
        projectId
    ], ()=>ProjectAPI.getProjectMembersMeta(projectId), options);
};
// @TODO commented as part of removing get-members api. Remove this hook after confirming that there is no impact
/* export const useAllProjectMembershipsQuery = (): UseQueryResult<
  ProjectMembership[]
> => {
  const dispatch = useDispatch();

  return useQuery<ProjectMembership[]>(
    'ALL_PROJECT_MEMBERSHIPS',
    () => MembersAPI.getAllProjectMemberships(),
    {
      onSuccess: (memberships) => {
        dispatch(
          setLoadedSeveral(
            memberships.map(({ projectId, members, teamList }) => ({
              projectId: ProjectId(projectId),
              memberships: members.map(
                ({
                  user: { userId },
                  permissionSet: { permissionSetType: memberType },
                  joinedAt,
                }) => ({
                  userId: UserId(userId),
                  memberType,
                  joinedAt,
                }),
              ),
              placeholders: teamList,
            })),
          ),
        );
      },
    },
  );
}; */ export { queryClient } from './client';
